/** Work schedule and time balances of an employee. */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { InputNumber } from "@primereact/ui/inputnumber";
import { apiGet, apiPut } from "../../lib/api.js";
import { Detail, MutationError, Section } from "../shared.js";
import type { EmployeeProfileData } from "./types.js";
import { WEEKDAYS, minutesLabel } from "./employee-common.js";

export function EmployeeTimeSettings({
  data,
  canManage,
}: {
  data: EmployeeProfileData;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const currentYear = new Date().getFullYear();
  const [schedule, setSchedule] = useState(data.schedule);
  const [year, setYear] = useState(currentYear);
  const balance = useQuery({
    queryKey: ["employee-balance", data.user.id, year],
    queryFn: () =>
      apiGet<{
        data: EmployeeProfileData["balances"][number];
      }>(`/employees/${data.user.id}/balances/${year}`).then(
        (response) => response.data,
      ),
  });
  const [vacationAdjustmentMinutes, setVacationAdjustmentMinutes] = useState(0);
  const [compensationAdjustmentMinutes, setCompensationAdjustmentMinutes] =
    useState(0);
  useEffect(() => {
    if (!balance.data) return;
    setVacationAdjustmentMinutes(balance.data.vacationAdjustmentMinutes);
    setCompensationAdjustmentMinutes(
      balance.data.compensationAdjustmentMinutes,
    );
  }, [balance.data]);
  const saveSchedule = useMutation({
    mutationFn: () =>
      apiPut(`/employees/${data.user.id}/schedule`, {
        days: schedule,
      }),
    onSuccess: async () =>
      client.invalidateQueries({
        queryKey: ["employee", data.user.id],
      }),
  });
  const weeklyMinutes = schedule.reduce(
    (total, day) => total + day.plannedMinutes,
    0,
  );
  const saveBalance = useMutation({
    mutationFn: () =>
      apiPut(`/employees/${data.user.id}/balances/${year}`, {
        year,
        vacationCarryoverMinutes: 0,
        vacationAdjustmentMinutes,
        compensationAdjustmentMinutes,
      }),
    onSuccess: async () =>
      client.invalidateQueries({
        queryKey: ["employee-balance", data.user.id, year],
      }),
  });
  return (
    <div>
      <div className="mb-5 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-app-border bg-app-surface p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-app-muted">
            {t("employees.weeklyHours", { defaultValue: "Weekly hours" })}
          </div>
          <div className="mt-1 text-2xl font-semibold text-app-text">
            {minutesLabel(weeklyMinutes)}
          </div>
        </div>
        <div className="rounded-xl border border-app-border bg-app-surface p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-app-muted">
            {t("employees.vacationRemaining", {
              defaultValue: "Vacation remaining",
            })}
          </div>
          <div className="mt-1 text-2xl font-semibold text-app-text">
            {minutesLabel(balance.data?.vacationRemainingMinutes ?? 0)}
          </div>
        </div>
        <div className="rounded-xl border border-app-border bg-app-surface p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-app-muted">
            {t("employees.compensationRemaining", {
              defaultValue: "Compensation remaining",
            })}
          </div>
          <div className="mt-1 text-2xl font-semibold text-app-text">
            {minutesLabel(balance.data?.compensationRemainingMinutes ?? 0)}
          </div>
        </div>
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <Section title={t("employees.weeklySchedule")}>
          <div className="flex flex-col space-y-2">
            {WEEKDAYS.map((day, index) => {
              const value =
                schedule.find((row) => row.weekday === index + 1)
                  ?.plannedMinutes ?? 0;
              return (
                <div
                  key={day}
                  className="flex justify-between whitespace-nowrap grow items-center gap-3"
                >
                  <label className="flex text-sm font-medium text-app-text">
                    {t(`weekdays.${day}`)}
                  </label>
                  <InputNumber.Root
                    fluid
                    min={0}
                    max={1440}
                    step={15}
                    disabled={!canManage}
                    value={value}
                    onValueChange={(event: { value: number | null }) =>
                      setSchedule((current) =>
                        current.map((row) =>
                          row.weekday === index + 1
                            ? {
                                ...row,
                                plannedMinutes: event.value ?? 0,
                              }
                            : row,
                        ),
                      )
                    }
                  >
                    <InputNumber.Input as={Input} />
                  </InputNumber.Root>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-app-muted">
            {t("employees.minutesHelp")}
          </p>
          {canManage ? (
            <>
              <MutationError error={saveSchedule.error} />
              <Button
                className="mt-4"
                onClick={() => saveSchedule.mutate()}
                loading={saveSchedule.isPending || undefined}
              >
                {t("common.save")}
              </Button>
            </>
          ) : null}
        </Section>
        <Section title={t("employees.balances")}>
          <div className="mb-4">
            <Field label={t("common.year")}>
              <InputNumber.Root
                fluid
                value={year}
                useGrouping={false}
                min={2000}
                max={2200}
                onValueChange={(event: { value: number | null }) => {
                  if (event.value) setYear(event.value);
                }}
              >
                <InputNumber.Input as={Input} />
              </InputNumber.Root>
            </Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Detail
              label={t("employees.vacationGranted")}
              value={minutesLabel(balance.data?.vacationGrantedMinutes ?? 0)}
            />
            <Detail
              label={t("employees.vacationUsed")}
              value={minutesLabel(balance.data?.vacationUsedMinutes ?? 0)}
            />
            <Detail
              label={t("employees.vacationRemaining")}
              value={minutesLabel(balance.data?.vacationRemainingMinutes ?? 0)}
            />
            <Detail
              label={t("employees.compensationEarned")}
              value={minutesLabel(balance.data?.compensationEarnedMinutes ?? 0)}
            />
            <Detail
              label={t("employees.compensationRemaining")}
              value={minutesLabel(
                balance.data?.compensationRemainingMinutes ?? 0,
              )}
            />
          </div>
          {canManage ? (
            <div className="mt-5 grid gap-3">
              <Field label={t("employees.vacationAdjustment")}>
                <InputNumber.Root
                  fluid
                  value={vacationAdjustmentMinutes}
                  onValueChange={(event: { value: number | null }) =>
                    setVacationAdjustmentMinutes(event.value ?? 0)
                  }
                >
                  <InputNumber.Input as={Input} />
                </InputNumber.Root>
              </Field>
              <Field label={t("employees.compensationAdjustment")}>
                <InputNumber.Root
                  fluid
                  value={compensationAdjustmentMinutes}
                  onValueChange={(event: { value: number | null }) =>
                    setCompensationAdjustmentMinutes(event.value ?? 0)
                  }
                >
                  <InputNumber.Input as={Input} />
                </InputNumber.Root>
              </Field>
              <MutationError error={saveBalance.error} />
              <Button
                onClick={() => saveBalance.mutate()}
                loading={saveBalance.isPending || undefined}
              >
                {t("common.save")}
              </Button>
            </div>
          ) : null}
        </Section>
      </div>
    </div>
  );
}
